/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module link/ui/linkformview
 */

import {
	ButtonView,
	FocusCycler,
	LabeledFieldView,
	SwitchButtonView,
	View,
	ViewCollection,
	createLabeledInputText,
	injectCssTransitionDisabler,
	submitHandler
} from 'ckeditor5/src/ui';
import { FocusTracker, KeystrokeHandler } from 'ckeditor5/src/utils';
import { icons } from 'ckeditor5/src/core';

// See: #8833.
// eslint-disable-next-line ckeditor5-rules/ckeditor-imports
import '@ckeditor/ckeditor5-ui/theme/components/responsive-form/responsiveform.css';
// import MediaLibraryFileLinkActionsView from "./media-library-file-link-actions-view";
// import '../../theme/linkform.css';

/**
 * The link form view controller class.
 *
 * See {@link module:link/ui/linkformview~LinkFormView}.
 *
 * @extends module:ui/view~View
 */

export default class MediaLibraryFileLinkFormView extends View {
	/**
	 * Creates an instance of the {@link module:link/ui/linkformview~LinkFormView} class.
	 *
	 * Also see {@link #render}.
	 *
	 * @param {module:utils/locale~Locale} [locale] The localization services instance.
	 * @param {module:link/linkcommand~LinkCommand} linkCommand Reference to {@link module:link/linkcommand~LinkCommand}.
	 * @param {String} [protocol] A value of a protocol to be displayed in the input's placeholder.
	 */
	constructor( locale, linkCommand ) {
		super( locale );

		const t = locale.t;

		/**
		 * Tracks information about DOM focus in the form.
		 *
		 * @readonly
		 * @member {module:utils/focustracker~FocusTracker}
		 */
		this.focusTracker = new FocusTracker();

		/**
		 * An instance of the {@link module:utils/keystrokehandler~KeystrokeHandler}.
		 *
		 * @readonly
		 * @member {module:utils/keystrokehandler~KeystrokeHandler}
		 */
		this.keystrokes = new KeystrokeHandler();

		// /**
		//  * The URL input view.
		//  *
		//  * @member {module:ui/labeledfield/labeledfieldview~LabeledFieldView}
		//  */
		//this.urlInputView = this._createUrlInput();


		// /**
		//  * The file title input view.
		//  * This is used as the link text.
		//  *
		//  * @type {*}
		//  */
		// this.fileTitleInput = this._createInput('Link text');

		/**
		 * The Download as field optionally defines what the file should be downloaded as.
		 *
		 * @type {*}
		 */
		this.fileDownloadNameInput = this._createInput('Download as');

		/**
		 * The file id.
		 *
		 * @type {*}
		 */
		this.fileIdHidden = this._createHidden('fileId');

		/**
		 * The file URL.
		 *
		 * @type {*}
		 */
		this.fileUrlHidden = this._createHidden('fileUrl');

		/**
		 * The file link text.
		 *
		 * @type {*}
		 */
		this.fileLinkTextHidden = this._createHidden('fileLinkText');

		/**
		 * The Save button
		 * @type {module:ui/button/buttonview~ButtonView}
		 */
		this.saveButton = this._createButton( 'Save', icons.check, 'ck-button-save' );
		this.saveButton.type = 'submit';

		/**
		 * The Cancel button
		 *
		 * @type {module:ui/button/buttonview~ButtonView}
		 */
		this.cancelButton = this._createButton( 'Cancel', icons.cancel, 'ck-button-cancel' );
		this.cancelButton.delegate( 'execute' ).to( this, 'cancel' );

		/**
		 * The Upload button - opens the file uploader in a new tab
		 *
		 * @type {string}
		 */
		const uploadIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M212.7 4.688l-144 144c-6.25 6.25-6.25 16.38 0 22.62s16.38 6.25 22.62 0L208 54.63V336c0 8.844 7.156 16 16 16s16-7.156 16-16V54.63l116.7 116.7c6.25 6.25 16.38 6.25 22.62 0s6.25-16.38 0-22.62l-144-144C232.2 1.562 228.1 0 224 0S215.8 1.562 212.7 4.688zM416 336v96c0 26.47-21.53 48-48 48h-288C53.53 480 32 458.5 32 432v-96C32 327.2 24.84 320 16 320S0 327.2 0 336v96C0 476.1 35.88 512 80 512h288c44.13 0 80-35.88 80-80v-96c0-8.844-7.156-16-16-16S416 327.2 416 336z"/></svg>';
		this.uploadButton = this._createButton( 'Upload', uploadIcon, 'ck-button-upload' );
		this.uploadButton.delegate( 'execute' ).to( this, 'upload' );

		/**
		 * The refresh button - reloads the content of the file picker
		 *
		 * @type {string}
		 */
		const refreshIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M463.5 272.1C463.2 272 462.9 272 462.6 272c-4.031 0-7.469 3-7.938 7.094C443.1 379.9 357.7 456 256 456c-85.8 0-161.6-55.56-188.9-136H184C188.4 320 192 316.4 192 312S188.4 304 184 304H48c-4.406 0-8 3.594-8 8v128C40 444.4 43.59 448 48 448s8-3.594 8-8v-104C88.43 416.9 167.2 472 256 472c109.8 0 202.1-82.16 214.6-191.1C471.1 276.5 467.9 272.6 463.5 272.1zM464 64c-4.406 0-8 3.594-8 8v104C423.6 95.09 344.8 40 256 40c-109.8 0-202.1 82.16-214.6 191.1c-.5 4.375 2.625 8.344 7.031 8.844C48.78 239.1 49.09 240 49.38 240c4.031 0 7.469-3 7.938-7.094C68.91 132.1 154.3 56 256 56c85.8 0 161.6 55.56 188.9 136H328C323.6 192 320 195.6 320 200s3.594 8 8 8h136c4.406 0 8-3.594 8-8v-128C472 67.59 468.4 64 464 64z"/></svg>';
		this.refreshButton = this._createButton( 'Refresh', refreshIcon, 'ck-button-refresh' );
		this.refreshButton.delegate( 'execute' ).to( this, 'refresh' );

		/**
		 * The File picker panel
		 */
		this.filePanelView = this._createFilePanel();

		/**
		 * The File picker controls panel
		 */
		this.fileFormView = this._createFileForm();


		// /**
		//  * The Save button view.
		//  *
		//  * @member {module:ui/button/buttonview~ButtonView}
		//  */
		// this.saveButtonView = this._createButton( t( 'Save' ), icons.check, 'ck-button-save' );
		// this.saveButtonView.type = 'submit';
		//
		// /**
		//  * The Cancel button view.
		//  *
		//  * @member {module:ui/button/buttonview~ButtonView}
		//  */
		// this.cancelButtonView = this._createButton( t( 'Cancel' ), icons.cancel, 'ck-button-cancel', 'cancel' );

		// /**
		//  * A collection of {@link module:ui/button/switchbuttonview~SwitchButtonView},
		//  * which corresponds to {@link module:link/linkcommand~LinkCommand#manualDecorators manual decorators}
		//  * configured in the editor.
		//  *
		//  * @private
		//  * @readonly
		//  * @type {module:ui/viewcollection~ViewCollection}
		//  */
		//this._manualDecoratorSwitches = this._createManualDecoratorSwitches( linkCommand );

		// /**
		//  * A collection of child views in the form.
		//  *
		//  * @readonly
		//  * @type {module:ui/viewcollection~ViewCollection}
		//  */
		//this.children = this._createFormChildren( linkCommand.manualDecorators );

		/**
		 * A collection of views that can be focused in the form.
		 *
		 * @readonly
		 * @protected
		 * @member {module:ui/viewcollection~ViewCollection}
		 */
		this._focusables = new ViewCollection();

		/**
		 * Helps cycling over {@link #_focusables} in the form.
		 *
		 * @readonly
		 * @protected
		 * @member {module:ui/focuscycler~FocusCycler}
		 */
		this._focusCycler = new FocusCycler( {
			focusables: this._focusables,
			focusTracker: this.focusTracker,
			keystrokeHandler: this.keystrokes,
			actions: {
				// Navigate form fields backwards using the Shift + Tab keystroke.
				focusPrevious: 'shift + tab',

				// Navigate form fields forwards using the Tab key.
				focusNext: 'tab'
			}
		} );

		const classList = [ 'ck', 'ck-link-form', 'ck-responsive-form' ];

		// if ( linkCommand.manualDecorators.length ) {
		// 	classList.push( 'ck-link-form_layout-vertical', 'ck-vertical-form' );
		// }

		this.childViews = this.createCollection( [
			this.filePanelView,
			this.fileFormView
		] );

		this.setTemplate( {
			tag: 'form',

			attributes: {
				class: classList,

				// https://github.com/ckeditor/ckeditor5-link/issues/90
				tabindex: '-1'
			},

			children: this.childViews
		} );

		//injectCssTransitionDisabler( this );
	}

	// /**
	//  * Obtains the state of the {@link module:ui/button/switchbuttonview~SwitchButtonView switch buttons} representing
	//  * {@link module:link/linkcommand~LinkCommand#manualDecorators manual link decorators}
	//  * in the {@link module:link/ui/linkformview~LinkFormView}.
	//  *
	//  * @returns {Object.<String,Boolean>} Key-value pairs, where the key is the name of the decorator and the value is
	//  * its state.
	//  */
	// getDecoratorSwitchesState() {
	// 	return Array.from( this._manualDecoratorSwitches ).reduce( ( accumulator, switchButton ) => {
	// 		accumulator[ switchButton.name ] = switchButton.isOn;
	// 		return accumulator;
	// 	}, {} );
	// }

	/**
	 * @inheritDoc
	 */
	render() {

		super.render();

		submitHandler( {
			view: this
		} );

		const childViews = [
			this.saveButton,
			this.cancelButton,
			this.uploadButton,
			this.refreshButton
		];

		// const childViews = [
		// 	this.filePanelView,
		// 	this.fileFormControls
		// 	// this.urlInputView,
		// 	// ...this._manualDecoratorSwitches,
		// 	// this.saveButtonView,
		// 	// this.cancelButtonView
		// ];

		// this.fileFormControls.forEach( v => {
		// 	// Register the view as focusable.
		// 	this._focusables.add( v );
		// 	// Register the view in the focus tracker.
		// 	this.focusTracker.add( v.element );
		// } );

		childViews.forEach( v => {
			// Register the view as focusable.
			this._focusables.add( v );

			// Register the view in the focus tracker.
			this.focusTracker.add( v.element );
		} );

		// Start listening for the keystrokes coming from #element.
		this.keystrokes.listenTo( this.element );
	}

	/**
	 * @inheritDoc
	 */
	destroy() {
		super.destroy();

		this.focusTracker.destroy();
		this.keystrokes.destroy();
	}

	/**
	 * Focuses the fist {@link #_focusables} in the form.
	 */
	focus() {
		this._focusCycler.focusFirst();
	}

	_createFilePanel() {

		this.filePanelView = new View();

		this.filePanelView.setTemplate({
			tag: 'div',
			attributes: {
				class: 'ck-media-library-file-panel',
			}
		});

		return this.filePanelView;

	}

	_createFileForm() {

		this.fileFormControls = new View();

		this.fileFormControls.setTemplate({
			tag: 'div',
			attributes: {
				class: 'ck-media-library-file-form-controls',
			},
			children: [
				this.saveButton,
				this.cancelButton,
				this.uploadButton,
				this.refreshButton
			]
		});

		this.fileFormView = new View();

		this.fileFormView.setTemplate({
			tag: 'div',
			attributes: {
				class: 'ck-media-library-file-form',
			},
			children: [
				this.fileDownloadNameInput,
				this.fileFormControls,
				this.fileLinkTextHidden,
				this.fileIdHidden,
				this.fileUrlHidden
			]
		});

		return this.fileFormView;

	}

	// /**
	//  * Creates a labeled input view.
	//  *
	//  * @private
	//  * @returns {module:ui/labeledfield/labeledfieldview~LabeledFieldView} Labeled field view instance.
	//  */
	// _createUrlInput() {
	// 	const t = this.locale.t;
	// 	const labeledInput = new LabeledFieldView( this.locale, createLabeledInputText );
	//
	// 	labeledInput.label = t( 'Link URL' );
	//
	// 	return labeledInput;
	// }

	/**
	 * Creates a labeled input view.
	 *
	 * @param label
	 * @returns {*}
	 * @private
	 */
	_createInput( label ) {

		const labeledInput = new LabeledFieldView( this.locale, createLabeledInputText );
		labeledInput.label = label;
		labeledInput.class = label.toLowerCase();
		return labeledInput;
	}

	/**
	 * Creates a button view.
	 *
	 * @private
	 * @param {String} label The button label.
	 * @param {String} icon The button icon.
	 * @param {String} className The additional button CSS class name.
	 * @param {String} [eventName] An event name that the `ButtonView#execute` event will be delegated to.
	 * @returns {module:ui/button/buttonview~ButtonView} The button view instance.
	 */
	_createButton( label, icon, className, eventName ) {
		const button = new ButtonView( this.locale );

		button.set( {
			label,
			icon,
			tooltip: true
		} );

		button.extendTemplate( {
			attributes: {
				class: className,
				title: label
			}
		} );

		if ( eventName ) {
			button.delegate( 'execute' ).to( this, eventName );
		}

		return button;
	}

	/**
	 * Creates a hidden labeled input view.
	 *
	 * @param label
	 * @returns {*}
	 * @private
	 */
	_createHidden( label ) {

		const labeledInput = new LabeledFieldView( this.locale, createLabeledInputText );
		labeledInput.class = 'ck-hidden ' + label;
		return labeledInput;

	}

	// /**
	//  * Populates {@link module:ui/viewcollection~ViewCollection} of {@link module:ui/button/switchbuttonview~SwitchButtonView}
	//  * made based on {@link module:link/linkcommand~LinkCommand#manualDecorators}.
	//  *
	//  * @private
	//  * @param {module:link/linkcommand~LinkCommand} linkCommand A reference to the link command.
	//  * @returns {module:ui/viewcollection~ViewCollection} of switch buttons.
	//  */
	// _createManualDecoratorSwitches( linkCommand ) {
	// 	const switches = this.createCollection();
	//
	// 	for ( const manualDecorator of linkCommand.manualDecorators ) {
	// 		const switchButton = new SwitchButtonView( this.locale );
	//
	// 		switchButton.set( {
	// 			name: manualDecorator.id,
	// 			label: manualDecorator.label,
	// 			withText: true
	// 		} );
	//
	// 		switchButton.bind( 'isOn' ).toMany( [ manualDecorator, linkCommand ], 'value', ( decoratorValue, commandValue ) => {
	// 			return commandValue === undefined && decoratorValue === undefined ? manualDecorator.defaultValue : decoratorValue;
	// 		} );
	//
	// 		switchButton.on( 'execute', () => {
	// 			manualDecorator.set( 'value', !switchButton.isOn );
	// 		} );
	//
	// 		switches.add( switchButton );
	// 	}
	//
	// 	return switches;
	// }


	// /**
	//  * Populates the {@link #children} collection of the form.
	//  *
	//  * If {@link module:link/linkcommand~LinkCommand#manualDecorators manual decorators} are configured in the editor, it creates an
	//  * additional `View` wrapping all {@link #_manualDecoratorSwitches} switch buttons corresponding
	//  * to these decorators.
	//  *
	//  * @private
	//  * @param {module:utils/collection~Collection} manualDecorators A reference to
	//  * the collection of manual decorators stored in the link command.
	//  * @returns {module:ui/viewcollection~ViewCollection} The children of link form view.
	//  */
	// _createFormChildren( manualDecorators ) {
	// 	const children = this.createCollection();
	//
	// 	children.add( this.urlInputView );
	//
	// 	// if ( manualDecorators.length ) {
	// 	// 	const additionalButtonsView = new View();
	// 	//
	// 	// 	additionalButtonsView.setTemplate( {
	// 	// 		tag: 'ul',
	// 	// 		children: this._manualDecoratorSwitches.map( switchButton => ( {
	// 	// 			tag: 'li',
	// 	// 			children: [ switchButton ],
	// 	// 			attributes: {
	// 	// 				class: [
	// 	// 					'ck',
	// 	// 					'ck-list__item'
	// 	// 				]
	// 	// 			}
	// 	// 		} ) ),
	// 	// 		attributes: {
	// 	// 			class: [
	// 	// 				'ck',
	// 	// 				'ck-reset',
	// 	// 				'ck-list'
	// 	// 			]
	// 	// 		}
	// 	// 	} );
	// 	// 	children.add( additionalButtonsView );
	// 	// }
	//
	// 	children.add( this.saveButtonView );
	// 	children.add( this.cancelButtonView );
	//
	// 	return children;
	// }

}

/**
 * Fired when the form view is submitted (when one of the children triggered the submit event),
 * for example with a click on {@link #saveButtonView}.
 *
 * @event submit
 */

/**
 * Fired when the form view is canceled, for example with a click on {@link #cancelButtonView}.
 *
 * @event cancel
 */
